import { range } from "../../../core/util/FunctionalUtils";
import { noteToFrequency } from "../../../core/util/MathUtil";
import { MasterComposer, MusicalComposer } from "../Composers/MasterComposer";
import { MusicalNote } from "../MusicalNote";
import { Instrument } from "./Instrument";

/**
 * Bass instrument using triangle wave synthesis.
 *
 * Creates warm, foundational bass sounds using triangle waves with
 * ADSR envelopes. Synthesizes notes generated by BassComposer
 * which provides phrase-aware, harmonic bass lines.
 *
 * Audio characteristics:
 * - Waveform: Triangle (warm, less harsh than sawtooth)
 * - Envelope: ADSR with quick attack, moderate decay, sustain, quick release
 * - Range: E1 to E3 (28-52 MIDI notes)
 * - Role: Harmonic foundation with chord tone emphasis
 */
export class BassInstrument extends Instrument {
  tags = ["bassInstrument"];

  // Type-safe parameter fields
  protected attackTime: number = 0.01;
  protected decayTime: number = 0.1;
  protected sustainLevel: number = 0.7;
  protected releaseTime: number = 0.05;

  constructor(audioContext: AudioContext, output: GainNode) {
    super(audioContext, output);
    this.initializeParameters();
  }

  /**
   * Initialize bass-specific parameters.
   */
  protected initializeParameters(): void {
    this.addParameter(
      () => this.attackTime,
      (value) => {
        this.attackTime = value;
      },
      {
        key: "attackTime",
        label: "Attack Time",
        min: 0.001,
        max: 0.1,
        defaultValue: 0.01,
        step: 0.001,
        unit: "s",
        category: "Envelope",
      },
    );

    this.addParameter(
      () => this.decayTime,
      (value) => {
        this.decayTime = value;
      },
      {
        key: "decayTime",
        label: "Decay Time",
        min: 0.01,
        max: 0.5,
        defaultValue: 0.1,
        step: 0.01,
        unit: "s",
        category: "Envelope",
      },
    );

    this.addParameter(
      () => this.sustainLevel,
      (value) => {
        this.sustainLevel = value;
      },
      {
        key: "sustainLevel",
        label: "Sustain Level",
        min: 0.1,
        max: 1.0,
        defaultValue: 0.7,
        step: 0.01,
        category: "Envelope",
      },
    );

    this.addParameter(
      () => this.releaseTime,
      (value) => {
        this.releaseTime = value;
      },
      {
        key: "releaseTime",
        label: "Release Time",
        min: 0.01,
        max: 0.2,
        defaultValue: 0.05,
        step: 0.01,
        unit: "s",
        category: "Envelope",
      },
    );
  }

  /**
   * Get valid MIDI notes for bass instrument.
   */
  getValidNotes(): number[] {
    return range(28, 52); // E1 to E3 for deep bass foundation
  }

  /**
   * Play a single bass note using triangle wave synthesis with ADSR envelope.
   */
  playNote(note: MusicalNote, startTime: number, duration: number): void {
    // Create oscillator and gain node for this note
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    // Configure triangle wave oscillator
    oscillator.type = "triangle";
    oscillator.frequency.value = noteToFrequency(note.noteNumber);

    // ADSR envelope configuration
    const attackTime = 0.01; // 10ms attack - quick but not instant
    const decayTime = 0.1; // 100ms decay
    const sustainLevel = 0.7; // 70% sustain level
    const releaseTime = 0.05; // 50ms release

    // Calculate volume from velocity and ensure good bass presence
    const baseVolume = 0.8; // Strong bass presence
    const volume = baseVolume * (note.velocity ?? 1.0);

    // Set up ADSR envelope
    gainNode.gain.setValueAtTime(0, startTime);

    // Attack
    gainNode.gain.linearRampToValueAtTime(volume, startTime + attackTime);

    // Decay to sustain level
    gainNode.gain.exponentialRampToValueAtTime(
      volume * sustainLevel,
      startTime + attackTime + decayTime,
    );

    // Sustain (hold current level during note duration)
    const sustainEndTime = startTime + duration - releaseTime;
    gainNode.gain.setValueAtTime(
      volume * sustainLevel,
      Math.max(sustainEndTime, startTime + attackTime + decayTime),
    );

    // Release
    gainNode.gain.exponentialRampToValueAtTime(
      0.01, // Very quiet, not zero to avoid audio glitches
      startTime + duration,
    );

    // Connect audio graph: oscillator -> gain -> instrument gain chain
    oscillator.connect(gainNode);
    gainNode.connect(this.gain);

    // Clean up nodes after playback to prevent memory leaks
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };

    // Schedule playback
    oscillator.start(startTime);
    oscillator.stop(startTime + duration);
  }
}
